import React from 'react';
import { Controller, type Control, type FieldErrors, type FieldArrayWithId } from 'react-hook-form';
import { WeightInput } from '../common/WeightInput';
import { AutocompleteSelect, type AutocompleteOption } from '../common/AutocompleteSelect';
import type { FinishedGoodsProductionFormValues, FinalProductMode } from '../../hooks/inventory-hooks/useFinishedGoodsProductionForm';
import type { WeightUnit } from '../../hooks/useWeightUnit';
import { useLanguage } from '../../features/language/useLanguage';

interface FinishedGoodsComponentsSectionProps {
  control: Control<FinishedGoodsProductionFormValues>;
  errors: FieldErrors<FinishedGoodsProductionFormValues>;
  fields: FieldArrayWithId<FinishedGoodsProductionFormValues, 'components', 'rowId'>[];
  components: FinishedGoodsProductionFormValues['components'];
  unit: WeightUnit;
  finalProductMode: FinalProductMode;
  packSizeKg: number;
  finalQuantityPcs: number;
  totalGrams: number;
  componentQuantityInputs: Record<string, { percentage?: string; grams?: string }>;
  handleAddComponent: () => void;
  handleComponentSelectChange: (index: number, value: string) => void;
  handlePercentageChange: (index: number, raw: string) => void;
  handleComponentWeightChange: (index: number, valKg: number | undefined) => void;
  handleFinalComponentWeightChange?: (index: number, valKg: number | undefined) => void;
  availableOptionsForRow: (index: number) => AutocompleteOption[];
  getRoastedBulkLabel?: (itemId?: string, roastType?: string) => string | undefined;
  remove: (index: number) => void;
  readOnly?: boolean;
  showFinalAllocation?: boolean;
}

export const FinishedGoodsComponentsSection: React.FC<FinishedGoodsComponentsSectionProps> = ({
  control,
  errors,
  fields,
  components,
  unit,
  finalProductMode,
  packSizeKg,
  finalQuantityPcs,
  totalGrams,
  componentQuantityInputs,
  handleAddComponent,
  handleComponentSelectChange,
  availableOptionsForRow,
  getRoastedBulkLabel,
  handlePercentageChange,
  handleComponentWeightChange,
  handleFinalComponentWeightChange,
  remove,
  readOnly = false,
  showFinalAllocation = false,
}) => {
  const { t } = useLanguage();
  return (
  <div className="space-y-3">
    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-2">
      <h3 className="text-sm font-medium text-gray-900">{t('inventory.finishedGoods.componentsTitle')}</h3>
      <div className="flex items-center gap-2">
        {!readOnly && !showFinalAllocation && (
          <button
            type="button"
            data-test-id="finished-goods-add-component"
            onClick={handleAddComponent}
            className="inline-flex items-center rounded-md border border-gray-300 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 shadow-sm hover:bg-gray-200 transition-colors ease-in-out duration-200"
          >
            {t('inventory.finishedGoods.actions.addComponent')}
          </button>
        )}
      </div>
    </div>

    {finalProductMode === 'new' &&
      totalGrams - packSizeKg * 1000 * (finalQuantityPcs > 0 ? finalQuantityPcs : 1) > 0.5 &&
      packSizeKg > 0 &&
      !readOnly && (
        <div
          className="relative mb-4 rounded-md border px-3 py-2 border-red-400"
          data-test-id="finished-goods-packsize-exceeded-warning"
        >
          <div className="absolute inset-0 rounded-md bg-red-200 animate-pulse pointer-events-none opacity-20" />
          <div className="relative z-10">
            <div className="flex items-baseline justify-between">
              <span className="text-sm font-medium text-gray-700">{t('inventory.finishedGoods.errors.allocationExceeds')}</span>
              <span className="text-sm font-mono text-gray-900">
                Over:{' '}
                {(
                  (totalGrams - packSizeKg * 1000 * (finalQuantityPcs > 0 ? finalQuantityPcs : 1)) /
                  1000
                ).toFixed(3)}{' '}
                kg
              </span>
            </div>
            <p className="mt-1 text-xs text-red-600">
              {t('inventory.finishedGoods.errors.totalAllocationPrefix')} ({totalGrams > 0 ? (totalGrams / 1000).toFixed(3) : 0} kg) {t('inventory.finishedGoods.errors.totalAllocationSuffix')} (
              {(packSizeKg * (finalQuantityPcs > 0 ? finalQuantityPcs : 1)).toFixed(3)} kg).
            </p>
          </div>
        </div>
      )}

    {Math.abs(totalGrams - packSizeKg * 1000 * finalQuantityPcs) > 0.5 && packSizeKg > 0 && finalQuantityPcs > 0 && !readOnly && (
      <div className="relative mb-4 rounded-md border px-3 py-2 border-red-400">
        <div className="absolute inset-0 rounded-md bg-red-200 animate-pulse pointer-events-none opacity-20" />
        <div className="relative z-10">
          <div className="flex items-baseline justify-between">
            <span className="text-sm font-medium text-gray-700">{t('inventory.finishedGoods.errors.totalInputVsTarget')}</span>
            <span className="text-sm font-mono text-gray-900">
              Diff: {(totalGrams / 1000 - packSizeKg * finalQuantityPcs).toFixed(3)} kg
            </span>
          </div>
          <p className="mt-1 text-xs text-red-600">
            {t('inventory.finishedGoods.errors.totalInputWeightPrefix')} ({totalGrams > 0 ? (totalGrams / 1000).toFixed(3) : 0} kg) {t('inventory.finishedGoods.errors.totalInputWeightSuffix')} (
            {(packSizeKg * finalQuantityPcs).toFixed(3)} kg).
          </p>
        </div>
      </div>
    )}

    <div className="space-y-2">
      {fields.length > 0 && (
        <div className={`hidden md:grid gap-3 px-3 mb-2 font-medium text-sm text-gray-700 ${showFinalAllocation ? 'md:grid-cols-12' : 'md:grid-cols-11'}`}>
          <div className={`${!readOnly ? 'md:col-span-3' : 'md:col-span-5'}`}>{t('inventory.finishedGoods.columns.roastedBulkItem')}</div>
          <div className="md:col-span-2">{t('inventory.finishedGoods.columns.roastType')}</div>
          {!readOnly && <div className={`${showFinalAllocation ? 'md:col-span-1' : 'md:col-span-2'}`}>{t('inventory.finishedGoods.columns.availableKg')}</div>}
          <div className="md:col-span-3 relative flex items-center h-full">
            <span className="font-medium text-sm text-gray-700">{t('inventory.finishedGoods.columns.allocation')}</span>
            {fields.some((_, idx) => (Number(components[idx]?.quantityKg) || 0) > (Number(components[idx]?.availableKg) || 0)) && !readOnly && (
              <span className="absolute left-20 -ml-2 whitespace-nowrap text-[10px] font-bold text-red-600 bg-red-50 px-1.5 py-0.5 rounded border border-red-200 animate-in fade-in duration-300">
                {t('inventory.finishedGoods.errors.stockExceeded')}
              </span>
            )}
          </div>
          {showFinalAllocation && <div className="md:col-span-2">{t('inventory.finishedGoods.columns.finalAllocation')}</div>}
          <div className="md:col-span-1"></div>
        </div>
      )}
      {fields.map((field, index) => {
        const component = components[index];
        const availableKg = Number(component?.availableKg) || 0;
        return (
          <div
            key={field.rowId}
            className={`grid grid-cols-1 gap-3 items-start md:items-center rounded-xl border border-gray-200 p-4 transition-all duration-200 hover:border-primary-300 hover:shadow-md bg-white ${showFinalAllocation ? 'md:grid-cols-12' : 'md:grid-cols-11'}`}
            data-test-id={`finished-goods-component-row-${index}`}
          >
            <div className={`${!readOnly ? 'md:col-span-3' : 'md:col-span-5'} space-y-1`}>
              <label className="block text-sm font-medium text-gray-700 md:hidden">{t('inventory.finishedGoods.columns.roastedBulkItem')}</label>
              <AutocompleteSelect
                value={component?.roastedBulkItemId && component?.roastType ? `${component.roastedBulkItemId}:${component.roastType}` : ''}
                displayValueOverride={
                  availableOptionsForRow(index).find(o => o.id === `${component?.roastedBulkItemId}:${component?.roastType}`)?.text
                  ?? getRoastedBulkLabel?.(component?.roastedBulkItemId, component?.roastType)
                  ?? component?.itemName
                }
                onChange={(_, option) => handleComponentSelectChange(index, option?.id || '')}
                options={availableOptionsForRow(index)}
                disabled={readOnly || showFinalAllocation}
                placeholder={t('inventory.finishedGoods.placeholders.selectRoastedBulk')}
                allowCustom={false}
                optionClassName="whitespace-nowrap pr-4"
                optionsClassName="min-w-full w-max"
                dataTestId={`finished-goods-component-select-${index}`}
              />
              {errors.components?.[index]?.roastedBulkItemId && (
                <p className="text-sm text-red-600">{errors.components[index]?.roastedBulkItemId?.message}</p>
              )}
            </div>

            <div className="md:col-span-2 space-y-1">
              <span className="block text-sm font-medium text-gray-700 md:hidden">{t('inventory.finishedGoods.columns.roastType')}</span>
              <div className="text-sm text-gray-900 uppercase h-9 flex items-center" data-test-id={`finished-goods-component-roast-${index}`}>
                {component?.roastType || '-'}
              </div>
            </div>

            {!readOnly && (
              <div className={`${showFinalAllocation ? 'md:col-span-1' : 'md:col-span-2'} space-y-1`}>
                <span className="block text-sm font-medium text-gray-700 md:hidden">{t('inventory.finishedGoods.columns.availableKg')}</span>
                <div className="text-sm text-gray-900 h-9 flex items-center" data-test-id={`finished-goods-component-available-${index}`}>
                  {availableKg ? availableKg.toFixed(3) : '0.000'}
                </div>
              </div>
            )}

            <div className="md:col-span-3 space-y-1">
              <span className="block text-sm font-medium text-gray-700 md:hidden">{t('inventory.finishedGoods.columns.allocation')}</span>
              <div className="flex items-center gap-2">
                <div className="relative flex-1 min-w-0">
                  <input
                    type="text"
                    inputMode="decimal"
                    placeholder="0"
                    value={
                      (component?.id ? componentQuantityInputs[component.id]?.percentage : undefined) ??
                      (component?.percentage ? component.percentage.toString() : '')
                    }
                    onChange={event => handlePercentageChange(index, event.target.value)}
                    disabled={readOnly || showFinalAllocation}
                    data-test-id={`finished-goods-component-percentage-${index}`}
                    className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-2 py-2 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  />
                </div>
                <span className="text-sm text-gray-500 whitespace-nowrap">%</span>
                <div className="relative flex-1 min-w-0">
                  <Controller
                    control={control}
                    name={`components.${index}.gramsPerPack`}
                    render={({ field: fieldValue }) => (
                      <WeightInput
                        value={fieldValue.value !== undefined && fieldValue.value !== null ? fieldValue.value / 1000 : undefined}
                        onChange={(valKg) => handleComponentWeightChange(index, valKg)}
                        unit={unit}
                        showUnitLabel={false}
                        disabled={readOnly || showFinalAllocation}
                        className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-2 py-2"
                        placeholder="0"
                        data-test-id={`finished-goods-component-grams-input-${index}`}
                      />
                    )}
                  />
                </div>
                <span className="text-sm text-gray-500 whitespace-nowrap">{unit}</span>
              </div>
            </div>
            {showFinalAllocation && (
              <div className="md:col-span-2 space-y-1">
                <span className="block text-sm font-medium text-gray-700 md:hidden">{t('inventory.finishedGoods.columns.finalAllocation')}</span>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 min-w-0">
                    <Controller
                      control={control}
                      name={`components.${index}.finalGramsPerPack`}
                      render={({ field: fieldValue }) => (
                        <WeightInput
                          value={
                            fieldValue.value !== undefined && fieldValue.value !== null && Number(fieldValue.value) > 0
                              ? Number(fieldValue.value) / 1000
                              : (component?.gramsPerPack ? component.gramsPerPack / 1000 : undefined)
                          }
                          onChange={(valKg) => handleFinalComponentWeightChange?.(index, valKg)}
                          unit={unit}
                          showUnitLabel={false}
                          disabled={readOnly}
                          className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm px-2 py-2"
                          placeholder="0"
                          data-test-id={`finished-goods-component-final-grams-input-${index}`}
                        />
                      )}
                    />
                  </div>
                  <span className="text-sm text-gray-500 whitespace-nowrap">{unit}</span>
                </div>
              </div>
            )}

            {!readOnly && !showFinalAllocation && (
              <div className="md:col-span-1 flex justify-center md:justify-end">
                <button
                  type="button"
                  onClick={() => remove(index)}
                  className="w-full md:w-auto inline-flex justify-center items-center rounded-md md:rounded-full border border-red-200 md:border-none bg-red-50 md:bg-transparent px-3 py-2 md:p-2 text-sm font-medium text-red-700 md:text-red-600 shadow-sm md:shadow-none hover:bg-red-100 md:hover:bg-red-50 transition-colors"
                  title={t('inventory.finishedGoods.actions.removeComponent')}
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                  </svg>
                  <span className="md:hidden ml-2">{t('inventory.finishedGoods.actions.removeComponent')}</span>
                </button>
              </div>
            )}
          </div>
        );
      })}

      {errors.components && typeof errors.components?.message === 'string' && (
        <p className="mt-1 text-xs text-red-600" data-test-id="finished-goods-components-error">
          {errors.components.message}
        </p>
      )}

      {fields.length === 0 && (
        <div className="rounded-md border border-dashed border-gray-300 p-3 text-xs text-gray-500">
          {t('inventory.finishedGoods.empty')}
        </div>
      )}
    </div>
  </div>
  );
};
